import React, { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Div100vh from "react-div-100vh";
import { onboardingFormDataAtom } from "@/recoil/atom/onboardingFormDataAtom";
import PrimaryButton from "@/components/common/buttons/PrimaryButton";
import Header from "@/components/common/Header/header";
import DynamicInputForm from "../DynamicInputForm";
import config from "./config";
import constants from "@/config";
import { motion } from "framer-motion";
import { headingVariants } from "@/lib/data";
import RejectedScreen from "@/components/journey/RejectedScreen";
import { useRecoilValue } from "recoil";
import { userDataAtom } from "@/recoil/atom/userDataAtom";
import SafeIcon from "@/assets/icons/safe-icon.svg";
import RBI from "@/assets/icons/rbi_logo.png";
import { CONSENT_MESSAGES } from "@/lib/data";
import { trackConsent } from "@/lib/apis";
import { handleAndTrackError } from "@/lib/helper";
import LoadingSplashScreen from "@/components/onboarding/LoadingSplashScreen";

const NewDynamicScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userData = useRecoilValue(userDataAtom);

  // console.log(location);

  const { screenId } = useParams();
  const screenConfig = config.find((screen) => screen.path === `/${screenId}`);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useRecoilState(onboardingFormDataAtom);
  const [disableNextBtn, setDisableNextBtn] = useState(false);
  const [loadingText, setLoadingText] = useState("Loading...");
  console.log(formData.pan_number, screenConfig);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const { pdata, alternateNumbers, afterCheck = null } = location.state || {};

  const [linkDrawer, setLinkDrawer] = useState(false);
  const [currLink, setCurrLink] = useState("");

  useEffect(() => {
    const checkEligibility = async () => {
      if (screenConfig?.path === "/landlord-details") {
        if (formData?.phone && formData?.phone === userData?.phone) {
          setError("You can't enter your number.");
        } else if (
          formData?.phone &&
          formData?.phone !== userData?.phone &&
          userData?.rent
        ) {
          setError("");
        }
      } else if (screenConfig?.path === "/eligibility-check") {
        try {
          setApprovalLoading((prevState) => !prevState);
          const signupToken = localStorage.getItem("SIGNUP_TOKEN_CP");
          const authorizationHeader = `Bearer ${signupToken}`;

          const res = await fetch(`${constants.API_URL}/v2/approval/check`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-event-type": "user/record-consent",
              Authorization: authorizationHeader,
            },
          });

          if (!res.ok) {
            throw new Error("Request failed");
          }

          const data = await res.json();
          console.log(data?.data?.isEligible);
          // leave only on a real decision; NEED_PAN + any error stays for PAN entry
          if (afterCheck) {
            navigate(afterCheck);
          } else if (
            ["SUCCESS", "NTC_USER", "FAILED"].includes(data?.data?.status)
          ) {
            window.location.href = "/";
          }

          console.log(data);
        } catch (error) {
          console.error("Error in eligibility check:", error);
        } finally {
          setApprovalLoading((prevState) => !prevState);
        }
      }
    };

    checkEligibility();
  }, []); // Add all dependencies here

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (loading) {
      const loadingMessages = [
        "Loading...",
        "Almost there...",
        "Finishing up...",
        "Almost done...",
      ];
      let index = 0;

      // Change loading text at intervals
      const interval = setInterval(() => {
        index = (index + 1) % loadingMessages.length;
        setLoadingText(loadingMessages[index]);
      }, 3000); // Change text every 1 second

      // Clear interval on unmount or when loading stops
      return () => clearInterval(interval);
    }
  }, [loading]);

  useEffect(() => {
    if (error) {
      setError("");
    }
    setDisableNextBtn(false);
  }, [formData]);

  const handleClick = async () => {
    if (screenConfig.validateText?.(formData)) {
      setError("Please enter correct details.");
    } else if (screenConfig.validate(formData) && !disableNextBtn) {
      try {
        setLoading((prevState) => !prevState);
        const requiredFormData = {};

        if (screenId === "alt-phone") {
          requiredFormData["pan_number"] = pdata["pan_number"];
          requiredFormData["pincode"] = pdata["pincode"];
        }
        screenConfig.fields.forEach((field) => {
          if (formData.hasOwnProperty(field.name)) {
            if (screenId === "alt-phone")
              requiredFormData["alt_phone"] = formData[field.name];
            else requiredFormData[field.name] = formData[field.name];
          }
        });

        let status = await screenConfig.onSubmit(
          requiredFormData,
          screenConfig.apiPath,
          screenConfig.click_name,
          screenConfig.click_source
        );

        const platform =
          navigator?.userAgentData?.platform ||
          navigator.platform ||
          localStorage.getItem("UTM_SOURCE");

        switch (screenConfig.apiPath) {
          case "approval/check":
            await trackConsent(
              "Onclick",
              "PAN and Bureau",
              formData.pan_number,
              platform,
              CONSENT_MESSAGES.PAN_AND_BUREAU
            );
            break;
          default:
            break;
        }

        // Do not change the order below.
        if (screenConfig.apiPath === "user/save-as-landlord")
          return navigate("/", { state: { from: "update-landlord" } });

        if (formData?.leadType === "landlord")
          return navigate("/update-landlord", { state: { from: "about" } });

        navigate(screenConfig?.nextPath, {
          state: {
            redirectPath: screenConfig?.redirectPath,
            res: status,
            pdata: requiredFormData,
          },
        });
      } catch (err) {
        handleAndTrackError(error, {
          apiPath: screenConfig.apiPath,
        });
      } finally {
        setLoading((prevState) => !prevState);
      }
    } else if (disableNextBtn) setError("Please try again after some time!");
    else {
      setError("Please enter all required details!");
    }
  };

  const handleHome = () => {
    if (screenConfig?.backPath) {
      navigate(screenConfig.backPath);
    }
  };

  if (approvalLoading) return <LoadingSplashScreen />;
  return (
    <Div100vh className="flex flex-col justify-start p-6 sm:p-8 gap-6">
      <Header
        progress={screenConfig?.progress?.progress}
        total={screenConfig?.progress?.total}
        customBackSubmit={screenConfig?.backPath ? handleHome : undefined}
      />
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-1">
          <motion.h1
            className="reveal-heading"
            initial="hidden"
            animate="visible"
            variants={headingVariants}
          >
            <span className="heading mb-heading-subheading">
              {screenConfig?.heading}
            </span>
          </motion.h1>
          {screenConfig?.subheading && (
            <motion.h1
              className="reveal-heading"
              initial="hidden"
              animate="visible"
              variants={headingVariants}
            >
              <span
                className={`${
                  screenConfig.subheadingSize || "text-xs"
                } text-[#999999]`}
              >
                {screenConfig?.subheading}
                {alternateNumbers &&
                  alternateNumbers.map((number, index) => (
                    <span key={index} className="text-[#666666]">
                      {number}
                      {index < alternateNumbers.length - 1 ? " / " : ""}
                    </span>
                  ))}
              </span>
            </motion.h1>
          )}
        </div>
        <DynamicInputForm
          formConfig={screenConfig?.fields}
          formData={formData}
          setFormData={setFormData}
          setError={setError}
          setDisableNextBtn={setDisableNextBtn}
        />
      </div>

      <div className="flex flex-col h-full items-end justify-end">
        {screenConfig?.consent && (
          <div className="flex flex-col items-center mb-2">
            {/* checkBox 1 */}
            {/* <div className="flex items-start space-x-2 text-2xs mb-4">
              
              <div
                className={`w-4 h-4 border-2 aspect-square mt-0.5 ${
                  formData?.isChecked ? " bg-[#eee]" : "border-gray-400"
                } flex items-center justify-center cursor-pointer`}
                onClick={() =>
                  setFormData((prevData) => ({
                    ...prevData,
                    ["isChecked"]:
                      formData?.isChecked !== null
                        ? !formData?.isChecked
                        : false,
                  }))
                }
              >
                {formData?.isChecked && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 text-black"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={3}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </div>

              
              <label
                htmlFor="termsCheckbox"
                className=" text-custom-light-grey-color  leading-relaxed"
              >
                I hereby authorize and give my consent to Fexprime Finance Pvt.
                Ltd. (“FFPL”) to download my KYC records from the Central KYC
                Registry (CKYCR) for the purpose of verification of my identity
                and address from the CKYCR database.
              </label>
            </div> */}
            {/* checkbox 2 */}

            <span className="text-[11px] text-custom-light-grey-color text-center m-2">
              {CONSENT_MESSAGES.PAN_AND_BUREAU}
            </span>
          </div>
        )}
        <div className="flex w-full ">
          <PrimaryButton
            text={loading ? loadingText : screenConfig.click_name || "Next"}
            handleClick={handleClick}
            error={error}
            disabled={loading || error !== ""}
          />
        </div>
      </div>
    </Div100vh>
  );
};

export default NewDynamicScreen;
